import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const VendorReceivedMessages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    if (user) {
      fetchReceivedMessages();
    }
  }, [user]);

  const fetchReceivedMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/favorites/messages/received', { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setMessages(data || []);
      }
    } catch (error) {
      console.error('Error fetching received messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please login to view your messages</p>
      </div>
    );
  }

  return (
    <div className="vendor-received-messages">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-gray-700">📬 Replies from Buyers</h4>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="border rounded-lg p-4 animate-pulse bg-gray-50 h-20" />
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="text-4xl mb-2">📭</p>
          <p className="text-gray-600">No replies from buyers yet</p>
          <p className="text-sm text-gray-400 mt-1">
            When buyers reply to your messages, they'll appear here
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((message) => (
            <div 
              key={message._id}
              className="border rounded-lg overflow-hidden hover:shadow-md transition cursor-pointer bg-white"
              onClick={() => setSelectedMessage(message)}
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {message.subject}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-xs text-gray-500">
                        From: {message.from || 'Buyer'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(message.sentAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {message.content}
                    </p>
                  </div>
                  <span className="text-gray-400 text-sm">→</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h3 className="font-semibold text-lg">{selectedMessage.subject}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-gray-600">
                    From: {selectedMessage.from || 'Buyer'}
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatDate(selectedMessage.sentAt)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedMessage(null)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 flex-1 overflow-y-auto">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-800 whitespace-pre-wrap">
                  {selectedMessage.content}
                </p>
              </div>
              {selectedMessage.fromEmail && (
                <div className="mt-4 text-xs text-gray-400">
                  Buyer email: {selectedMessage.fromEmail}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorReceivedMessages;
